import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Plyr from 'plyr-react';
import 'plyr-react/dist/plyr.css';
import useScreenSize from '../../core/hooks/useScreenSize';
import ArticleSort from '../ArticleSort/ArticleSort';
import ArticleQuestion from '../ArticleQuestion/ArticleQuestion';
import Heading from '../Heading/Heading';
import CategoryLink from '../CategoryLink/CategoryLink';
import ArticleLikeButton from '../ArticleLikeButton/ArticleLikeButton';
import { Icon } from '../common/Icons';
import Wrapper from '../common/Wrapper/Wrapper';
import {
  StyledContainer,
  StyledArticle,
  StyledAuthorContent,
  StyledAuthorName,
  StyledAuthorInfo,
  StyledTitle,
  StyledDescription,
  StyledMoreButton,
  StyledSpacer,
  StyledPlayerCont,
} from './ArticleList.styles';

function ArticleList({ articles, sortComponent, title }) {
  const [size] = useScreenSize();

  const CategoryLinkElement = useCallback(
    ({ item, isMobile }) => {
      const displayCondition = isMobile === size < 992;
      if (!item.category || !displayCondition) return null;

      return (
        <CategoryLink
          link={item.category.link && `/category/${item.category.link}`}
          color={item.category.color && item.category.color}
          name={item.category.name && item.category.name}
        />
      );
    },
    [size]
  );

  if (!articles[0]) return null;

  return (
    <StyledContainer>
      {sortComponent && <ArticleSort />}
      {title && (
        <Wrapper>
          <Heading icon={<Icon.Saved aria-hidden />} title={title} styling="font-14-semi-bold" />
        </Wrapper>
      )}
      {articles.map(article => (
        <StyledArticle key={article.id}>
          {article.video && (
            <StyledPlayerCont>
              <Plyr
                source={{
                  type: 'video',
                  title: article.video.title || 'Video title',
                  sources: [
                    {
                      src: article.video.src || '',
                      type: 'video/mp4',
                      provider: article.video.provider || '',
                    },
                  ],
                  poster: article.video.thumbnail || false,
                }}
              />
            </StyledPlayerCont>
          )}
          <Wrapper>
            {CategoryLinkElement({ item: article, isMobile: true })}

            <StyledAuthorContent>
              {article.author && (
                <StyledAuthorInfo>
                  <StyledAuthorName>{article.author}</StyledAuthorName>
                  {article.postTime && <span>{article.postTime}</span>}
                  {CategoryLinkElement({ item: article, isMobile: false })}
                </StyledAuthorInfo>
              )}
              {article.bookmarked !== null && <ArticleLikeButton id={article.articleId} />}
            </StyledAuthorContent>
            {article.title && <StyledTitle>{article.title}</StyledTitle>}
            {article.description && (
              <StyledDescription>
                {article.description}
                {article.moreBtn && <StyledMoreButton>{article.moreBtn}</StyledMoreButton>}
              </StyledDescription>
            )}
            {article.question && <ArticleQuestion question={article.question} id={article.id} />}
          </Wrapper>
          <StyledSpacer />
        </StyledArticle>
      ))}
    </StyledContainer>
  );
}

ArticleList.propTypes = {
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      video: PropTypes.shape({
        provider: PropTypes.string.isRequired,
        src: PropTypes.string.isRequired,
        thumbnail: PropTypes.string,
        title: PropTypes.string,
      }),
      category: PropTypes.shape({
        name: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        link: PropTypes.string.isRequired,
      }),
      author: PropTypes.string,
      postTime: PropTypes.string,
      bookmarked: PropTypes.bool,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      moreBtn: PropTypes.string,
    })
  ),
};

ArticleList.defaultProps = {
  articles: [],
};

export default ArticleList;
